/**
 * Composed travel-day stages from booked or owner-confirmed facts only.
 * Never invents durations, boat times, or meeting points.
 * A transfer does not hide other day items, and a missing boatIncluded flag
 * is not evidence that a boat is required.
 * Arrival check-in follows inbound travel (by the hotel's from-time) and
 * stays before later clocked plans. The window is not treated as an appointment.
 */

import type { TripDocument, TripEvent } from "./schema.ts";
import { itemsForDate } from "./itinerary.ts";
import { clockHm, formatDateLabel } from "./time.ts";

export type TravelStageKind =
  | "leave_stay"
  | "flight"
  | "arrive"
  | "bags_meeting"
  | "transfer"
  | "pier"
  | "boat"
  | "check_in"
  | "excursion"
  | "free_time"
  | "other";

export type TravelStage = {
  id: string;
  kind: TravelStageKind;
  title: string;
  detail: string | null;
  timeLabel: string | null;
  unknown: boolean;
  href?: string;
  actionLabel?: string;
};

const ISLAND_CHAPTER = /samet/i;
const PIER_PLACE_IDS = new Set(["ban-phe"]);

export function isTravelDay(doc: TripDocument, date: string): boolean {
  if (doc.flights.some((flight) =>
    flight.departure.local.slice(0, 10) === date || flight.arrival.local.slice(0, 10) === date
  )) return true;
  if (doc.events.some((event) => event.kind === "transfer" && !event.archived && event.date === date)) return true;
  return doc.stays.some((stay) => stay.endDate === date);
}

function hm(local: string | null | undefined): string | null {
  return clockHm(local);
}

function islandStay(doc: TripDocument, predicate: (stay: TripDocument["stays"][number]) => boolean) {
  return doc.stays.find((stay) => ISLAND_CHAPTER.test(stay.chapter) && predicate(stay));
}

function isPierPlace(placeId: string | null | undefined): boolean {
  return Boolean(placeId && PIER_PLACE_IDS.has(placeId));
}

function isIslandPlace(doc: TripDocument, placeId: string | null | undefined): boolean {
  if (!placeId) return false;
  const stay = doc.stays.find((row) => row.placeId === placeId);
  if (stay && ISLAND_CHAPTER.test(stay.chapter)) return true;
  const place = doc.places.find((row) => row.id === placeId);
  return Boolean(place && ISLAND_CHAPTER.test(place.name));
}

export function transferIncludesBoat(doc: TripDocument, event: TripEvent): boolean {
  if (event.includesBoat === true) return true;
  if (event.includesBoat === false) return false;
  if (isPierPlace(event.fromPlaceId) || isPierPlace(event.toPlaceId)) return true;
  const fromIsland = isIslandPlace(doc, event.fromPlaceId);
  const toIsland = isIslandPlace(doc, event.toPlaceId);
  if (fromIsland !== toIsland && (event.fromPlaceId || event.toPlaceId) && event.fromPlaceId && event.toPlaceId) {
    return fromIsland || toIsland;
  }
  return false;
}

export function boatBeforeTransfer(doc: TripDocument, event: TripEvent): boolean {
  const leavingIsland = Boolean(islandStay(doc, (stay) => stay.endDate === event.date));
  const arrivingIsland = Boolean(islandStay(doc, (stay) => stay.startDate === event.date));
  if (leavingIsland && !arrivingIsland) return true;
  if (isIslandPlace(doc, event.fromPlaceId) && !isIslandPlace(doc, event.toPlaceId)) return true;
  return false;
}

function methodTitle(event: TripEvent, bookingTitle: string | null | undefined): string {
  if (event.title) return event.title;
  if (event.transferMethod === "grab_bolt") return "Grab or Bolt";
  if (event.transferMethod === "taxi") return "Taxi";
  if (event.transferMethod === "arranged_driver") return "Arranged driver";
  return bookingTitle ?? "Transfer";
}

function expandTransfer(doc: TripDocument, event: TripEvent): TravelStage[] {
  const booking = doc.bookings.find((row) => row.id === event.bookingId);
  const stages: TravelStage[] = [];
  const linked = doc.flights.find((flight) => flight.id === event.linkedFlightId || flight.id === event.flightId);
  const boat = transferIncludesBoat(doc, event);
  const fromPlace = event.fromPlaceId ? doc.places.find((row) => row.id === event.fromPlaceId) : undefined;
  const toPlace = event.toPlaceId ? doc.places.find((row) => row.id === event.toPlaceId) : undefined;

  if (linked && (event.pickupMode === "when_ready" || event.pickupMode === "after_arrival")) {
    stages.push({
      id: `meet-${event.id}`,
      kind: "bags_meeting",
      title: event.pickupMode === "when_ready" ? "Bags, then the meeting point" : "Pickup after landing",
      detail: event.pickupInstruction ?? event.meetingNote ?? null,
      timeLabel: event.pickupMode === "when_ready" || event.pickupMode === "after_arrival"
        ? "When you're ready"
        : hm(event.startLocal ?? event.plannedDeparture),
      unknown: false,
      href: `/journey/transfer/${event.id}`,
      actionLabel: "Pickup instructions",
    });
  }

  const transferStage: TravelStage = {
    id: `transfer-${event.id}`,
    kind: "transfer",
    title: methodTitle(event, booking?.provider ?? booking?.title),
    detail: [
      fromPlace || event.fromAirport ? `From ${fromPlace?.name ?? event.fromAirport}` : null,
      toPlace ? `to ${toPlace.name}` : null,
      event.pickupInstruction && !(linked && (event.pickupMode === "when_ready" || event.pickupMode === "after_arrival"))
        ? event.pickupInstruction
        : booking?.vehicle ?? null,
    ].filter(Boolean).join(" · ") || event.notes[0] || null,
    timeLabel: hm(event.startLocal ?? event.plannedDeparture)
      ?? (event.pickupMode === "when_ready" || event.pickupMode === "after_arrival" ? "When you're ready" : null),
    unknown: Boolean(event.dateConfidence?.includes("inferred"))
      || (!event.plannedDeparture && !event.startLocal && event.pickupMode !== "when_ready" && event.pickupMode !== "after_arrival"),
    href: `/journey/transfer/${event.id}`,
    actionLabel: booking?.contactId ? "Contact" : "Transfer details",
  };

  const pierPlace = isPierPlace(event.toPlaceId) ? toPlace : isPierPlace(event.fromPlaceId) ? fromPlace : undefined;
  const pierStage: TravelStage | null = pierPlace
    ? {
      id: `pier-${event.id}`,
      kind: "pier",
      title: pierPlace.name,
      detail: pierPlace.address ?? null,
      timeLabel: hm(event.plannedArrival),
      unknown: !pierPlace.address,
      href: `/driver/${pierPlace.id}`,
      actionLabel: "Directions",
    }
    : null;

  const boatStage: TravelStage | null = boat
    ? {
      id: `boat-${event.id}`,
      kind: "boat",
      title: "Boat crossing",
      detail: booking?.boatIncluded === false
        ? "Boat is not included in this transfer package."
        : "Boat tickets and the exact crossing remain unknown until verified.",
      timeLabel: null,
      unknown: true,
      href: `/journey/transfer/${event.id}`,
    }
    : null;

  if (boat && boatBeforeTransfer(doc, event)) {
    if (boatStage) stages.push(boatStage);
    if (pierStage && isPierPlace(event.fromPlaceId)) stages.push(pierStage);
    stages.push(transferStage);
  } else {
    stages.push(transferStage);
    if (pierStage && isPierPlace(event.toPlaceId)) stages.push(pierStage);
    if (boatStage) stages.push(boatStage);
  }
  return stages;
}

function checkInStage(doc: TripDocument, stay: TripDocument["stays"][number]): TravelStage {
  const place = doc.places.find((row) => row.id === stay.placeId);
  return {
    id: `stay-${stay.id}`,
    kind: "check_in",
    title: `Check in at ${place?.name ?? stay.chapter}`,
    detail: stay.checkInFrom
      ? `Check-in from ${stay.checkInFrom}. This is a window, not an appointment.`
      : "Check-in time is not on the booking yet.",
    timeLabel: stay.checkInFrom ? `From ${stay.checkInFrom}` : null,
    unknown: !stay.checkInFrom,
    href: `/journey/stay/${stay.id}`,
    actionLabel: "Stay details",
  };
}

function stageFromItem(doc: TripDocument, date: string, item: ReturnType<typeof itemsForDate>[number]): TravelStage[] {
  if (item.kind === "transfer" && item.event) return expandTransfer(doc, item.event);
  if (item.kind === "flight") {
    const flight = doc.flights.find((row) => row.id === item.id);
    if (!flight) return [];
    const stages: TravelStage[] = [];
    if (flight.departure.local.slice(0, 10) === date) {
      stages.push({
        id: `dep-${flight.id}`,
        kind: "flight",
        title: `${flight.number} departs ${flight.departure.airport}`,
        detail: flight.departure.terminal ? `Terminal ${flight.departure.terminal}` : "Imported schedule, not airline-verified.",
        timeLabel: hm(flight.departure.local),
        unknown: false,
        href: `/journey/flight/${flight.id}`,
        actionLabel: "Flight details",
      });
    }
    if (flight.arrival.local.slice(0, 10) === date) {
      stages.push({
        id: `arr-${flight.id}`,
        kind: "arrive",
        title: `${flight.number} arrives ${flight.arrival.airport}`,
        detail: flight.arrival.terminal ? `Terminal ${flight.arrival.terminal}` : "Scheduled arrival only. Landing, bags and pickup stay separate.",
        timeLabel: hm(flight.arrival.local),
        unknown: false,
        href: `/journey/flight/${flight.id}`,
        actionLabel: "Flight details",
      });
    }
    return stages;
  }
  if (item.kind === "accommodation") {
    const stay = doc.stays.find((row) => item.id === `acc-${row.id}-${date}`);
    if (!stay || stay.startDate !== date) return [];
    return [checkInStage(doc, stay)];
  }
  const kind: TravelStageKind = item.kind === "excursion"
    ? "excursion"
    : item.kind === "free_time"
      ? "free_time"
      : "other";
  return [{
    id: item.id,
    kind,
    title: item.title,
    detail: item.event?.notes[0] ?? null,
    timeLabel: item.timeLabel,
    unknown: item.flexible,
    href: item.href,
    actionLabel: "Open details",
  }];
}

export function composeTravelDay(doc: TripDocument, date: string): TravelStage[] {
  const stages: TravelStage[] = [];
  const seen = new Set<string>();

  function push(stage: TravelStage) {
    if (seen.has(stage.id)) return;
    seen.add(stage.id);
    stages.push(stage);
  }

  const leaving = doc.stays.find((stay) => stay.endDate === date);
  if (leaving) {
    const place = doc.places.find((row) => row.id === leaving.placeId);
    push({
      id: `leave-${leaving.id}`,
      kind: "leave_stay",
      title: `Leave ${place?.name ?? leaving.chapter}`,
      detail: leaving.plannedDepartureEvidence ?? "Our planned departure, not the hotel checkout rule.",
      timeLabel: hm(leaving.plannedDepartureLocal),
      unknown: !leaving.plannedDepartureLocal,
      href: `/journey/stay/${leaving.id}`,
      actionLabel: "Stay details",
    });
  }

  for (const item of itemsForDate(doc, date)) {
    for (const stage of stageFromItem(doc, date, item)) push(stage);
  }

  return stages;
}

export function travelDayLabel(doc: TripDocument, date: string): string {
  const leaving = doc.stays.find((stay) => stay.endDate === date);
  const arriving = doc.stays.find((stay) => stay.startDate === date);
  if (leaving && arriving) return `${leaving.chapter} to ${arriving.chapter}`;
  if (leaving) return `Leaving ${leaving.chapter}`;
  if (arriving) return `Arriving ${arriving.chapter}`;
  return formatDateLabel(date);
}
